import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, apiRequest, setPasswordConfirmer } from '../api/client';
import { useSession } from '../api/session';
import { useErrorMessage } from '../errors';
import { useLocale } from '../locale';

/**
 * The password step-up for sensitive operations (ADR-0046).
 *
 * Paying money out, revealing an account number, exporting a salary bank file, adopting the onboarding
 * treatment and executing a data reset need the password entered within the last few minutes. When the
 * server refuses with `PASSWORD_CONFIRMATION_REQUIRED`, the API client calls the confirmer registered
 * here; the person re-enters their password, the session is stamped, and the client retries the refused
 * request once. Cancelling leaves the operation refused and nothing changed. The password is kept in
 * this component's state only until the dialog closes.
 */
export function PasswordConfirmationHost() {
  const { t } = useLocale();
  const { session } = useSession();
  const errorMessage = useErrorMessage();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | undefined>();
  const settle = useRef<((confirmed: boolean) => void) | undefined>(undefined);

  useEffect(() => {
    setPasswordConfirmer(
      () =>
        new Promise<boolean>((resolve) => {
          settle.current = resolve;
          setPassword('');
          setError(undefined);
          setOpen(true);
        }),
    );
    return () => setPasswordConfirmer(undefined);
  }, []);

  function close(confirmed: boolean) {
    setOpen(false);
    setPassword('');
    settle.current?.(confirmed);
    settle.current = undefined;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending || password.length === 0) return;
    setPending(true);
    setError(undefined);
    try {
      await apiRequest('/api/v1/me/confirm-password', { method: 'POST', body: { password } });
      close(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, 'NETWORK_ERROR'));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => close(false)}
      aria-labelledby="password-confirmation-title"
      fullWidth
      maxWidth="xs"
    >
      <form onSubmit={(event) => void submit(event)} noValidate>
        <DialogTitle id="password-confirmation-title">{t('passwordConfirm.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('passwordConfirm.description')}
            </Typography>
            {session ? (
              <Typography variant="body2" dir="ltr" sx={{ textAlign: 'start' }}>
                {session.account.loginIdentifier}
              </Typography>
            ) : null}
            {error ? (
              <Alert severity="error" variant="outlined" role="alert">
                {errorMessage(error)}
              </Alert>
            ) : null}
            <TextField
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoFocus
              required
              autoComplete="current-password"
              slotProps={{ htmlInput: { dir: 'ltr' } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => close(false)}>{t('actions.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={pending || password.length === 0}
            aria-busy={pending || undefined}
          >
            {t('passwordConfirm.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
